import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Edit, Video, Image, Eye, X, ArrowUpDown, Move, ZoomIn, ZoomOut, RotateCcw, Save } from "lucide-react";
import { isYouTubeUrl, getYouTubeThumbnailUrl, getYouTubeEmbedUrl } from "@/utils/youtube";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface MediaCardProps {
  media: {
    id: string;
    title: string;
    description: string;
    url: string;
    selected_screens: string[];
    display_time: number;
    created_at: string;
    updated_at: string;
    last_updated_at: string;
    user_id: string;
    active: boolean;
    order_index?: number;
    position_config?: { scale: number; offsetX: number; offsetY: number } | null;
  };
  screens: {
    id: string;
    name: string;
  }[];
  position?: number; // Posição atual do card na lista
  totalCards?: number; // Total de cards para validação
  onDelete: (id: string) => void;
  onEdit: (media: MediaCardProps['media']) => void;
  onUpdatePosition?: (mediaId: string, newPosition: number) => void;
  onUpdatePositionConfig?: (mediaId: string, positionConfig: { scale: number; offsetX: number; offsetY: number }) => Promise<void>;
}

const MediaCard = ({ media, screens, position, totalCards, onDelete, onEdit, onUpdatePosition, onUpdatePositionConfig }: MediaCardProps) => {
  const { toast } = useToast();
  const [showCopiedPopup, setShowCopiedPopup] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isEditingPosition, setIsEditingPosition] = useState(false);
  const [tempPosition, setTempPosition] = useState(position || 1);

  // Estados para reposicionamento de imagem
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [scale, setScale] = useState(media.position_config?.scale ?? 1);
  const [offsetX, setOffsetX] = useState(media.position_config?.offsetX ?? 0);
  const [offsetY, setOffsetY] = useState(media.position_config?.offsetY ?? 0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSaving, setIsSaving] = useState(false);

  // Verificar se é tipo de mídia que permite reposicionamento (imagens)
  const canReposition = !isYouTubeUrl(media.url) &&
    (media.url.toLowerCase().includes('.jpg') ||
      media.url.toLowerCase().includes('.jpeg') ||
      media.url.toLowerCase().includes('.png') ||
      media.url.toLowerCase().includes('.webp') ||
      media.url.toLowerCase().includes('.gif'));

  // Reset dos valores quando o modal de preview abre
  useEffect(() => {
    if (showPreview) {
      setScale(media.position_config?.scale ?? 1);
      setOffsetX(media.position_config?.offsetX ?? 0);
      setOffsetY(media.position_config?.offsetY ?? 0);
      setIsRepositioning(false);
    }
    // Removido media.position_config das dependências para evitar reset visual
    // quando a prop é atualizada pelo pai (o que causava "pulo" para a posição antiga)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview]);

  // Handlers para arraste
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isRepositioning) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - offsetX,
      y: e.clientY - offsetY
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isRepositioning) return;
    const newOffsetX = e.clientX - dragStart.x;
    const newOffsetY = e.clientY - dragStart.y;
    const maxOffset = 500;
    setOffsetX(Math.max(-maxOffset, Math.min(maxOffset, newOffsetX)));
    setOffsetY(Math.max(-maxOffset, Math.min(maxOffset, newOffsetY)));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handler para zoom com scroll
  const handleWheel = (e: React.WheelEvent) => {
    if (!isRepositioning) return;

    // Prevenir propagação para evitar rolar a página
    // e.preventDefault() é chamado automaticamente pelo React em alguns casos,
    // mas aqui o mais seguro é stopPropagation para garantir foco no componente
    e.stopPropagation();

    const zoomIntensity = 0.1;
    // Se scroll para baixo (deltaY > 0), diminui zoom. Se para cima, aumenta.
    const direction = e.deltaY > 0 ? -1 : 1;

    const newScale = Math.max(1, Math.min(3, scale + (direction * zoomIntensity)));

    // Arredondar para uma casa decimal para evitar flutuações bizarras
    setScale(Math.round(newScale * 10) / 10);
  };

  // Handler para salvar posição
  const handleSavePosition = async () => {
    setIsSaving(true);
    try {
      const positionConfig = { scale, offsetX, offsetY };

      if (onUpdatePositionConfig) {
        // Usar callback se disponível
        await onUpdatePositionConfig(media.id, positionConfig);
      } else {
        // Salvar diretamente via Supabase
        const { error } = await supabase
          .from('medias')
          .update({ position_config: positionConfig })
          .eq('id', media.id);

        if (error) throw error;
      }

      toast({
        title: "Posição salva!",
        description: "O posicionamento da mídia foi salvo com sucesso.",
      });
      setIsRepositioning(false);
    } catch (error) {
      console.error('Erro ao salvar posição:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar o posicionamento.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handler para resetar posição
  const handleResetPosition = () => {
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
  };

  // Gerenciar scroll do body quando modal está aberto
  useEffect(() => {
    if (showPreview) {
      // Bloquear scroll e esconder outros elementos
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      // Adicionar listener para ESC
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setShowPreview(false);
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = 'unset';
        document.body.style.position = 'unset';
        document.body.style.width = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
    }
  }, [showPreview]);

  // Função para verificar se é um vídeo (não YouTube)
  const isVideoUrl = (url: string): boolean => {
    if (isYouTubeUrl(url)) return false;

    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.m4v', '.mkv'];
    const urlLower = url.toLowerCase();

    return videoExtensions.some(ext =>
      urlLower.includes(ext) ||
      urlLower.endsWith(ext)
    );
  };

  // Função para verificar se é um GIF
  const isGifUrl = (url: string): boolean => {
    const urlLower = url.toLowerCase();
    return urlLower.includes('.gif') || urlLower.endsWith('.gif');
  };

  const getScreenNames = () => {
    const selectedScreens = media.selected_screens || [];
    console.log('🎦 MediaCard - getScreenNames:', {
      mediaId: media.id,
      title: media.title,
      selected_screens: selectedScreens
    });
    return selectedScreens.map(screenId => {
      const screen = screens.find(s => s.id.toString() === screenId);
      return screen ? screen.name : `Tela ${screenId}`;
    });
  };

  // Função para obter a URL da thumbnail baseada no tipo de mídia
  const getThumbnailUrl = () => {
    if (isYouTubeUrl(media.url)) {
      return getYouTubeThumbnailUrl(media.url);
    }

    // Para vídeos (não YouTube), não usar URL - será renderizado como ícone
    if (isVideoUrl(media.url)) {
      return null;
    }

    // Para GIFs, não usar URL - será renderizado como ícone
    if (isGifUrl(media.url)) {
      return null;
    }

    // Para outras mídias (imagens, websites), usar a URL original
    return media.url;
  };

  // Funções para edição de posição
  const handlePositionEdit = () => {
    setTempPosition(position || 1);
    setIsEditingPosition(true);
  };

  const handlePositionSave = () => {
    if (tempPosition >= 1 && tempPosition <= (totalCards || 999) && onUpdatePosition) {
      onUpdatePosition(media.id, tempPosition);
    }
    setIsEditingPosition(false);
  };

  const handlePositionCancel = () => {
    setTempPosition(position || 1);
    setIsEditingPosition(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handlePositionSave();
    } else if (e.key === 'Escape') {
      handlePositionCancel();
    }
  };

  // Função para renderizar thumbnail baseada no tipo
  const renderThumbnail = () => {
    if (isVideoUrl(media.url)) {
      // Thumbnail genérica para vídeos com fundo verde
      return (
        <div className="w-full h-32 bg-gradient-to-br from-[#39b92c] to-[#2d8f22] rounded-md flex items-center justify-center">
          <div className="text-center text-white">
            <Video className="w-12 h-12 mx-auto mb-2" />
            <div className="text-sm font-medium">Vídeo</div>
          </div>
        </div>
      );
    }

    if (isGifUrl(media.url)) {
      // Thumbnail genérica para GIFs com fundo verde
      return (
        <div className="w-full h-32 bg-gradient-to-br from-[#39b92c] to-[#2d8f22] rounded-md flex items-center justify-center">
          <div className="text-center text-white">
            <Image className="w-12 h-12 mx-auto mb-2" />
            <div className="text-sm font-medium">GIF</div>
          </div>
        </div>
      );
    }

    // Para YouTube e outras mídias, usar imagem normal
    return (
      <>
        <img
          src={getThumbnailUrl() || media.url}
          alt={media.title}
          className="w-full h-32 object-cover rounded-md"
          onError={(e) => {
            // Fallback para ícone baseado no tipo se a imagem falhar
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            target.nextElementSibling?.classList.remove('hidden');
          }}
        />
        {/* Fallback icon */}
        <div className="hidden w-full h-32 bg-gray-100 rounded-md flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-2">
              {getMediaType() === 'YouTube' && '📺'}
              {getMediaType() === 'Imagem' && '🖼️'}
              {getMediaType() === 'Vídeo' && '🎥'}
              {getMediaType() === 'GIF' && '📄'}
              {getMediaType() === 'Website' && '🌐'}
            </div>
            <div className="text-sm">{getMediaType()}</div>
          </div>
        </div>
      </>
    );
  };

  // Função para obter o tipo de mídia para exibição
  const getMediaType = () => {
    if (isYouTubeUrl(media.url)) {
      return 'YouTube';
    }

    if (isGifUrl(media.url)) {
      return 'GIF';
    }

    if (isVideoUrl(media.url)) {
      return 'Vídeo';
    }

    const url = media.url.toLowerCase();
    if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.webp')) {
      return 'Imagem';
    }

    return 'Website';
  };

  // Função para verificar se deve exibir ícone de visualização (agora para todos os tipos)
  const shouldShowPreviewIcon = () => {
    return true;
  };

  const displayTime = media.display_time || 5000; // Valor padrão se não definido

  // Função para calcular dias de veiculação
  const getDiasVeiculacao = () => {
    if (!media.created_at) return null;
    const created = new Date(media.created_at);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDias >= 0 ? diffDias : 0;
  };

  return (
    <Card className="w-full max-w-sm mx-auto bg-white shadow-lg rounded-lg overflow-hidden relative">
      <CardContent className="p-4">
        {/* Thumbnail da mídia */}
        <div className="mb-4 relative group">
          {renderThumbnail()}
          {/* Botão de visualização */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPreview(true);
            }}
            className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1.5 opacity-70 hover:opacity-100 hover:bg-black/80 transition-all duration-200 cursor-pointer"
            title="Visualizar mídia"
          >
            <Eye className="w-3.5 h-3.5 text-white" />
          </button>
          {/* Indicador de posição configurada */}
          {media.position_config && (
            <div className="absolute bottom-2 left-2 bg-[#39b92c]/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-xs text-white flex items-center gap-1">
              <Move className="w-2.5 h-2.5" />
              <span>Reposicionada</span>
            </div>
          )}
        </div>

        {/* Informações básicas */}
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg text-gray-800 truncate flex-1">{media.title}</h3>
            <Badge variant="outline" className="text-xs ml-2 shrink-0">
              {getMediaType()}
            </Badge>
          </div>

          {/* Grupo/Telas */}
          <div className="flex flex-wrap gap-1">
            {getScreenNames().map((screenName, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {screenName}
              </Badge>
            ))}
          </div>

          {/* Data de criação */}
          <p className="text-sm text-gray-600">
            Criado em: {new Date(media.created_at).toLocaleDateString('pt-BR')}
          </p>

          {/* Tempo de exibição (apenas visualização) */}
          <p className="text-sm text-gray-600">
            Duração: {displayTime === 0 ? 'Mídia fixa' : `${displayTime / 1000} segundos`}
          </p>

          {/* Dias de veiculação */}
          {getDiasVeiculacao() !== null && (
            <p className="text-sm text-gray-600">
              Veiculando há: {getDiasVeiculacao()} dias
            </p>
          )}
        </div>

        {/* Área inferior com posição e botões de ação */}
        <div className="flex justify-between items-center mt-4">
          {/* Lado esquerdo: Posição */}
          <div className="flex items-center space-x-2">
            {position !== undefined && (
              <div className="flex items-center">
                {isEditingPosition ? (
                  <div className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-md px-2 py-1.5 shadow-md">
                    <input
                      type="number"
                      value={tempPosition}
                      onChange={(e) => setTempPosition(Math.max(1, parseInt(e.target.value) || 1))}
                      onKeyDown={handleKeyDown}
                      onBlur={handlePositionSave}
                      className="w-6 text-xs text-center bg-transparent border-none outline-none font-semibold text-gray-800"
                      min={1}
                      max={totalCards || 999}
                      autoFocus
                    />
                    <span className="text-xs text-gray-500">/{totalCards || 1}</span>
                  </div>
                ) : (
                  <button
                    onClick={handlePositionEdit}
                    className="group flex items-center gap-1.5 bg-slate-800/90 hover:bg-slate-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-md transition-all duration-150 shadow-sm hover:shadow backdrop-blur-sm border border-white/20"
                    title={`Posição ${position} - Clique para editar`}
                  >
                    <span>{position}</span>
                    <ArrowUpDown className="h-2.5 w-2.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Lado direito: Botões de ação */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(media)}
              className="flex items-center gap-1"
            >
              <Edit className="h-4 w-4" />
              Editar
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <Trash2 className="h-4 w-4" />
                  Excluir
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirmar exclusão</DialogTitle>
                  <DialogDescription>
                    Tem certeza que deseja excluir a mídia "{media.title}"? Essa ação não pode ser desfeita.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogTrigger asChild>
                    <Button variant="outline">Cancelar</Button>
                  </DialogTrigger>
                  <Button
                    variant="destructive"
                    onClick={() => onDelete(media.id)}
                  >
                    Confirmar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardContent>

      {/* Modal de Preview usando Portal para renderizar no body */}
      {showPreview && createPortal(
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{
            zIndex: 99999,
            position: 'fixed !important',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isDragging) {
              setShowPreview(false);
            }
          }}
        >
          <div
            className="relative bg-gray-900 rounded-xl shadow-2xl max-w-5xl max-h-[95vh] w-full overflow-hidden"
            style={{ zIndex: 100000 }}
          >
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
              <div className="flex items-center gap-2">
                {isGifUrl(media.url) ? (
                  <Image className="w-5 h-5 text-[#39b92c]" />
                ) : isVideoUrl(media.url) ? (
                  <Video className="w-5 h-5 text-[#39b92c]" />
                ) : isYouTubeUrl(media.url) ? (
                  <Video className="w-5 h-5 text-[#39b92c]" />
                ) : (
                  <Image className="w-5 h-5 text-[#39b92c]" />
                )}
                <h3 className="font-semibold text-white truncate">{media.title}</h3>
                <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">
                  {getMediaType()}
                </Badge>
                {isRepositioning && (
                  <Badge className="text-xs bg-[#39b92c] text-white">
                    Modo Edição
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Botão de ativar modo reposicionamento (apenas para imagens) */}
                {canReposition && !isRepositioning && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsRepositioning(true)}
                    className="bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:text-white"
                  >
                    <Move className="w-4 h-4 mr-2" />
                    Reposicionar
                  </Button>
                )}
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setIsRepositioning(false);
                  }}
                  className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                  style={{ zIndex: 100001 }}
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Área de Preview (Simulador do Player quando em modo reposicionamento) */}
            <div className="p-4 bg-gray-900">
              {isRepositioning && (
                <div className="flex items-center gap-2 mb-3 text-sm text-gray-400">
                  <Move className="w-4 h-4" />
                  <span>Arraste para mover • Use scroll ou slider para zoom</span>
                </div>
              )}

              <div
                className={`relative w-full bg-black rounded-lg overflow-hidden mx-auto ${isRepositioning ? 'ring-2 ring-[#39b92c]' : ''}`}
                style={{
                  aspectRatio: '16/9',
                  maxHeight: '55vh',
                  cursor: isRepositioning ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
              >
                {/* Guias visuais (apenas no modo reposicionamento) */}
                {isRepositioning && (
                  <div className="absolute inset-0 pointer-events-none z-10">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20" />
                    <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20" />
                    <div className="absolute inset-4 border border-dashed border-white/10 rounded" />
                  </div>
                )}

                {/* Conteúdo da mídia */}
                {isGifUrl(media.url) || (!isVideoUrl(media.url) && !isYouTubeUrl(media.url)) ? (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={isRepositioning ? {
                      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
                      transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                    } : {
                      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
                    }}
                  >
                    <img
                      src={media.url}
                      alt={media.title}
                      className={`select-none ${isRepositioning ? 'pointer-events-none' : ''}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      draggable={false}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZiNzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkVycm8gYW8gY2FycmVnYXI8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  </div>
                ) : isVideoUrl(media.url) ? (
                  <video
                    src={media.url}
                    controls
                    className="w-full h-full object-contain"
                  >
                    Seu navegador não suporta a reprodução de vídeo.
                  </video>
                ) : isYouTubeUrl(media.url) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(media.url) || media.url}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={media.title}
                  />
                ) : null}
              </div>
            </div>

            {/* Controles de Zoom (apenas no modo reposicionamento) */}
            {isRepositioning && (
              <div className="px-4 pb-2 bg-gray-900">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setScale(prev => Math.max(prev - 0.1, 0.5))}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <ZoomOut className="w-4 h-4 text-white" />
                  </button>
                  <div className="flex-1">
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.05"
                      value={scale}
                      onChange={(e) => setScale(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-[#39b92c]"
                    />
                  </div>
                  <button
                    onClick={() => setScale(prev => Math.min(prev + 0.1, 3))}
                    className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <ZoomIn className="w-4 h-4 text-white" />
                  </button>
                  <span className="text-sm text-gray-400 w-16 text-center">
                    {Math.round(scale * 100)}%
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>Posição: X: {Math.round(offsetX)}px, Y: {Math.round(offsetY)}px</span>
                </div>
              </div>
            )}

            {/* Botões de ação do modo reposicionamento */}
            {isRepositioning && (
              <div className="p-4 border-t border-gray-700 bg-gray-800 flex justify-between">
                <Button
                  variant="outline"
                  onClick={handleResetPosition}
                  className="bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Resetar
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsRepositioning(false);
                      setScale(media.position_config?.scale ?? 1);
                      setOffsetX(media.position_config?.offsetX ?? 0);
                      setOffsetY(media.position_config?.offsetY ?? 0);
                    }}
                    className="bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSavePosition}
                    disabled={isSaving}
                    className="bg-[#39b92c] hover:bg-[#2d8f22] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Salvando...' : 'Salvar Posição'}
                  </Button>
                </div>
              </div>
            )}

            {/* Informações adicionais (apenas quando NÃO está no modo reposicionamento) */}
            {!isRepositioning && (
              <div className="p-4 border-t border-gray-700 bg-gray-800">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-400">
                  <div>
                    <span className="font-medium text-gray-300">URL:</span>
                    <div className="flex items-center gap-2 break-all text-xs mt-1 bg-gray-700 p-2 rounded">
                      <span className="truncate">{media.url}</span>
                      <button
                        title="Copiar URL"
                        className="ml-2 p-1 rounded hover:bg-gray-600 transition-colors shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(media.url);
                          setShowCopiedPopup(true);
                          setTimeout(() => setShowCopiedPopup(false), 1800);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4 text-gray-400">
                          <rect x="9" y="9" width="13" height="13" rx="2" strokeWidth="2" stroke="currentColor" fill="none" />
                          <rect x="3" y="3" width="13" height="13" rx="2" strokeWidth="2" stroke="currentColor" fill="none" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-300">Duração:</span>
                    <div className="mt-1">
                      {displayTime === 0 ? 'Mídia fixa' : `${displayTime / 1000} segundos`}
                    </div>
                  </div>
                </div>
                {media.position_config && (
                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                    <Move className="w-3 h-3" />
                    <span>Mídia reposicionada: Zoom {Math.round(media.position_config.scale * 100)}%</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>, document.body
      )}

      {/* Popup de URL copiada - z-index alto para aparecer sobre o modal */}
      {showCopiedPopup && createPortal(
        <div className="fixed bottom-6 right-6 bg-white text-gray-800 text-sm px-4 py-3 rounded-lg shadow-xl border border-gray-200 animate-fade-in-out" style={{ zIndex: 100002 }}>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span className="font-medium">URL copiada!</span>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
};

export default MediaCard;
